/**
 * Detects user's location based on IP address and returns appropriate language code
 */

interface LocationData {
  country_code?: string;
  country?: string;
  country_name?: string;
  timezone?: string;
}

// Map country codes to language codes
const countryToLanguageMap: Record<string, string> = {
  // Spanish speaking countries
  ES: 'es', AR: 'es', MX: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
  EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
  SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', GQ: 'es',
  
  // Portuguese speaking countries
  BR: 'pt-BR', PT: 'pt-BR', AO: 'pt-BR', MZ: 'pt-BR',
  
  // French speaking countries
  FR: 'fr', BE: 'fr', CH: 'fr', CA: 'fr', LU: 'fr', MC: 'fr',
  CI: 'fr', CM: 'fr', CD: 'fr', SN: 'fr', ML: 'fr', NE: 'fr',
  BF: 'fr', BJ: 'fr', TG: 'fr', RW: 'fr', BI: 'fr', DJ: 'fr',
  CF: 'fr', CG: 'fr', GA: 'fr', GN: 'fr', MG: 'fr', TD: 'fr',
  
  // Indonesian
  ID: 'id',
  
  // Default to English for other countries
};

/**
 * Fetch location data from IP geolocation API
 */
async function fetchLocationFromIP(): Promise<LocationData | null> {
  try {
    // Using ipapi.co - free tier allows 1000 requests per day
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return null;
  }
}

/**
 * Alternative API: ipgeolocation.io (backup)
 */
async function fetchLocationFromIPGeolocation(): Promise<LocationData | null> {
  try {
    const response = await fetch('https://api.ipgeolocation.io/ipgeo?apiKey=free', {
      signal: AbortSignal.timeout(5000),
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return {
      country_code: data.country_code2,
      country_name: data.country_name,
      timezone: data.time_zone?.name,
    };
  } catch (error) {
    return null;
  }
}

/**
 * Alternative API: ip-api.com (backup)
 */
async function fetchLocationFromIPAPI(): Promise<LocationData | null> {
  try {
    const response = await fetch('http://ip-api.com/json/', {
      signal: AbortSignal.timeout(5000),
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return {
      country_code: data.countryCode,
      country_name: data.country,
      timezone: data.timezone,
    };
  } catch (error) {
    return null;
  }
}

/**
 * Get language code based on country code
 */
function getLanguageFromCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();
  return countryToLanguageMap[upperCode] || 'en';
}

/**
 * Detect user's language based on IP location
 * Falls back to browser language if IP detection fails
 */
export async function detectLanguageFromIP(): Promise<string> {
  try {
    // Try primary API
    let locationData = await fetchLocationFromIP();
    
    // If primary fails, try backup APIs
    if (!locationData) {
      locationData = await fetchLocationFromIPAPI();
    }
    
    if (!locationData) {
      locationData = await fetchLocationFromIPGeolocation();
    }
    
    // If we got location data, map to language
    if (locationData && locationData.country_code) {
      const detectedLanguage = getLanguageFromCountry(locationData.country_code);
      return detectedLanguage;
    }
    
    // Fallback to browser language
    return getBrowserLanguage();
  } catch (error) {
    return getBrowserLanguage();
  }
}

/**
 * Get browser language as fallback
 */
function getBrowserLanguage(): string {
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  // Map browser language codes to supported languages
  if (browserLang.startsWith('es')) return 'es';
  if (browserLang.startsWith('pt')) return 'pt-BR';
  if (browserLang.startsWith('fr')) return 'fr';
  if (browserLang.startsWith('id')) return 'id';
  
  return 'en'; // Default to English
}

/**
 * Check if location detection is cached
 */
export function getCachedLanguage(): string | null {
  try {
    const cached = localStorage.getItem('detectedLanguage');
    const timestamp = localStorage.getItem('detectedLanguageTime');
    
    // Cache for 7 days
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (age < sevenDays) {
        return cached;
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Cache detected language
 */
export function cacheDetectedLanguage(language: string): void {
  try {
    localStorage.setItem('detectedLanguage', language);
    localStorage.setItem('detectedLanguageTime', Date.now().toString());
  } catch (error) {
    // Silent fail
  }
}

/**
 * Get cached location data (for currency/pricing features)
 */
export function getCachedLocationData(): LocationData | null {
  try {
    const cached = localStorage.getItem('ipLocationData');
    const timestamp = localStorage.getItem('ipLocationDataTime');
    
    // Cache for 7 days
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (age < sevenDays) {
        return JSON.parse(cached);
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Cache location data (for currency/pricing features)
 */
export function cacheLocationData(locationData: LocationData): void {
  try {
    localStorage.setItem('ipLocationData', JSON.stringify(locationData));
    localStorage.setItem('ipLocationDataTime', Date.now().toString());
  } catch (error) {
    // Silent fail
  }
}

/**
 * Get user's location data (always available for currency/pricing, regardless of language preference)
 */
export async function getUserLocationData(): Promise<LocationData | null> {
  // Check cache first
  let locationData = getCachedLocationData();
  
  if (locationData) {
    return locationData;
  }
  
  // Fetch fresh data
  locationData = await fetchLocationFromIP();
  
  if (!locationData) {
    locationData = await fetchLocationFromIPAPI();
  }
  
  if (!locationData) {
    locationData = await fetchLocationFromIPGeolocation();
  }
  
  // Cache the result
  if (locationData) {
    cacheLocationData(locationData);
  }
  
  return locationData;
}

